import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

export const ChartModal: React.FC<ChartModalProps> = ({
  isOpen,
  onClose,
  children,
  title
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { products } = useSupabaseInventory();

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        // Only close if the click is truly outside the modal content
        const target = event.target as Element;
        const isInsideModal = modalRef.current.contains(target);
        const isInteractiveElement = target.closest('button, input, select, textarea, [role="button"], [role="menuitem"], [role="option"]');
        
        if (!isInsideModal && !isInteractiveElement) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
      <div 
        ref={modalRef}
        className="w-full h-full relative animate-in fade-in-0 zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="h-full flex flex-col bg-background shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            {title && (
              <h2 className="text-xl font-semibold">{title}</h2>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-auto hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <CardContent className="flex-1 overflow-hidden flex flex-col p-0">
            <div className="flex-1 overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="min-h-[500px] p-6">
                {React.Children.map(children, (child) => {
                if (React.isValidElement(child) && child.props) {
                  // Ajouter les props spécifiques pour le modal
                  const modalProps: any = { 
                    ...child.props,
                  };
                  
                  // Détecter le type de composant de manière plus robuste
                  const componentName = child.type?.toString();
                  const displayName = (child.type as any)?.displayName;
                  const name = (child.type as any)?.name;
                  
                  // Pour les graphiques de ventes, ajouter showComparativeStats et products
                  if (componentName?.includes('SalesChart') || displayName?.includes('SalesChart') || name?.includes('SalesChart')) {
                    modalProps.showComparativeStats = true;
                    modalProps.products = products;
                  }
                  
                  // Pour les graphiques de niveau de stock, ajouter showProductAnalysis
                  if (componentName?.includes('StockLevelChart') || displayName?.includes('StockLevelChart') || name?.includes('StockLevelChart')) {
                    modalProps.showProductAnalysis = true;
                  }
                  
                  // Pour les graphiques de retraits, ajouter showComparativeStats
                  if (componentName?.includes('WithdrawalsChart') || displayName?.includes('WithdrawalsChart') || name?.includes('WithdrawalsChart')) {
                    modalProps.showComparativeStats = true;
                  }
                  
                  // Pour les graphiques en camembert, ajouter showComparativeStats
                  if (componentName?.includes('SalesPieChart') || displayName?.includes('SalesPieChart') || name?.includes('SalesPieChart') ||
                      componentName?.includes('WithdrawalsPieChart') || displayName?.includes('WithdrawalsPieChart') || name?.includes('WithdrawalsPieChart')) {
                    modalProps.showComparativeStats = true;
                  }
                  
                  // Pour les tableaux de ventes, utiliser la version infinite scroll
                  if (componentName?.includes('SalesListTable') || displayName?.includes('SalesListTable') || name?.includes('SalesListTable')) {
                    modalProps.showComparativeStats = true;
                    modalProps.useInfiniteScroll = true;
                  }

                  return React.cloneElement(child, modalProps);
                }
                  return child;
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};